import type { MenuItem, SubItem } from '@/types/sidebar'

interface TitleResolverOptions {
  defaultTitle?: string
}

export class TitleResolver {
  private pathMap: Map<string, string>
  private defaultTitle: string

  constructor(menuItems: MenuItem[], options: TitleResolverOptions = {}) {
    this.pathMap = new Map()
    this.defaultTitle = options.defaultTitle || 'menu.dashboard'
    this.flattenMenu(menuItems)
  }

  private flattenMenu(items: (MenuItem | SubItem)[]) {
    for (const item of items) {
      if (item.path) {
        this.pathMap.set(item.path, item.label)
      }
      // Check if 'items' property exists and is an array before recursing
      if ('items' in item && item.items && Array.isArray(item.items) && item.items.length > 0) {
        this.flattenMenu(item.items)
      }
    }
  }

  /**
   * Resolution order (first match wins):
   * 1. Exact match in the API menu (admin-authored label wins over generic meta)
   * 2. `metaTitle` — the matched route's `meta.title` i18n key (non-menu pages)
   * 3. Ancestor match by recursively stripping path segments
   *    (e.g. /invoice-review/2060630 -> /invoice-review)
   * 4. Default title
   */
  public resolve(path: string, metaTitle?: string): string {
    // 1. Exact match in menu
    const exactMatch = this.pathMap.get(path)
    if (exactMatch) {
      return exactMatch
    }

    // 2. Route meta title (i18n key declared on the route itself)
    if (metaTitle) {
      return metaTitle
    }

    // 3. Recursive stripping for deep links under a menu item
    let currentPath = path
    while (currentPath !== '' && currentPath !== '/') {
      const lastSlashIndex = currentPath.lastIndexOf('/')
      if (lastSlashIndex <= 0) break // Stop if we are at root or invalid path

      currentPath = currentPath.substring(0, lastSlashIndex)

      const strippedMatch = this.pathMap.get(currentPath)
      if (strippedMatch) {
        return strippedMatch
      }
    }

    // 4. Return default if nothing found
    return this.defaultTitle
  }

  public updateMenu(menuItems: MenuItem[]) {
    this.pathMap.clear()
    this.flattenMenu(menuItems)
  }
}
